import { formatInTimeZone } from 'date-fns-tz';

import { PusdtCurrency } from './constants/currency';
import Num from './num';

export const earnSunsetBufferStartsAt = Date.parse('2026-09-11T09:00:00.000Z');
export const earnSunsetAt = Date.parse('2026-09-25T09:00:00.000Z');

export type EarnSunsetPhase = 'pre-buffer' | 'buffer' | 'hidden';

/** sessionStorage — client UI / bunja. Cookie is the Edge proxy source of truth. */
export const earnSunsetPhaseOverrideStorageKey = 'flipster.custom_flag.earn_sunset_phase';
export const earnSunsetPhaseOverrideCookieName = 'flipster.earn_sunset_phase';

export function parseEarnSunsetPhaseOverride(value: string | null | undefined): EarnSunsetPhase | null {
  if (value === 'pre-buffer' || value === 'buffer' || value === 'hidden') return value;
  return null;
}

/** Dev / squad / stg only. Never flipster.io or flipster.me. */
export function allowsEarnSunsetPhaseOverride(hostname: string): boolean {
  const host = hostname.split(':')[0] ?? hostname;
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.localhost') ||
    host.endsWith('.yoda.mobi') ||
    host.endsWith('.prex.website') ||
    host.endsWith('.prex.sh') ||
    host.endsWith('.prex.run')
  );
}

function readOverrideCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${earnSunsetPhaseOverrideCookieName}=`;
  const match = document.cookie.split('; ').find((part) => part.startsWith(prefix));
  if (!match) return null;
  return decodeURIComponent(match.slice(prefix.length));
}

function writeOverrideCookie(value: EarnSunsetPhase | null): void {
  if (typeof document === 'undefined') return;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  if (value == null) {
    document.cookie = `${earnSunsetPhaseOverrideCookieName}=; path=/; max-age=0; SameSite=Lax${secure}`;
    return;
  }
  document.cookie = `${earnSunsetPhaseOverrideCookieName}=${encodeURIComponent(value)}; path=/; SameSite=Lax${secure}`;
}

export function readEarnSunsetPhaseOverride(): EarnSunsetPhase | null {
  if (typeof window === 'undefined') return null;
  if (!allowsEarnSunsetPhaseOverride(window.location.hostname)) return null;
  return (
    parseEarnSunsetPhaseOverride(sessionStorage.getItem(earnSunsetPhaseOverrideStorageKey)) ??
    parseEarnSunsetPhaseOverride(readOverrideCookie())
  );
}

/** DevTools Flag tab. No-op on prod hosts. */
export function persistEarnSunsetPhaseOverride(phase: EarnSunsetPhase | null): void {
  if (typeof window === 'undefined') return;
  if (!allowsEarnSunsetPhaseOverride(window.location.hostname)) return;
  if (phase == null) {
    sessionStorage.removeItem(earnSunsetPhaseOverrideStorageKey);
  } else {
    sessionStorage.setItem(earnSunsetPhaseOverrideStorageKey, phase);
  }
  writeOverrideCookie(phase);
}

export function getEarnSunsetPhase(now = Date.now(), override?: EarnSunsetPhase | null): EarnSunsetPhase {
  const resolved = override === undefined ? readEarnSunsetPhaseOverride() : override;
  if (resolved) return resolved;
  if (now < earnSunsetBufferStartsAt) return 'pre-buffer';
  if (now < earnSunsetAt) return 'buffer';
  return 'hidden';
}

export function getEarnSunsetNextBoundary(now = Date.now(), override?: EarnSunsetPhase | null): number | null {
  const resolved = override === undefined ? readEarnSunsetPhaseOverride() : override;
  if (resolved) return null;
  const phase = getEarnSunsetPhase(now, null);
  if (phase === 'pre-buffer') return earnSunsetBufferStartsAt;
  if (phase === 'buffer') return earnSunsetAt;
  return null;
}

export function formatEarnSunsetDisplayDate(): string {
  return `${formatInTimeZone(earnSunsetAt, 'UTC', 'MMM d, HH:mm')} UTC`;
}

export function isFlipsterEarnWebPath(pathname: string): boolean {
  return pathname === '/earn' || pathname.startsWith('/earn/');
}

export function isFlipsterEarnPremiumUsdtWebPath(pathname: string): boolean {
  return pathname === '/earn/details/premium-usdt' || pathname.startsWith('/earn/details/premium-usdt/');
}

export function isFlipsterEarnWebviewSunsetPath(pathname: string): boolean {
  return (
    pathname === '/earn/get-premium-usdt' ||
    pathname === '/flipster-earn/premium-usdt' ||
    pathname.startsWith('/flipster-earn/premium-usdt/')
  );
}

export function isEarnSunsetProductCurrency(currency: string): boolean {
  return currency === PusdtCurrency;
}

export function isEarnSunsetHolder(
  wallets: Record<string, { walletBalanceExclWithdrawalPending?: string }> | null | undefined,
): boolean {
  if (!wallets) return false;
  return Num(wallets[PusdtCurrency]?.walletBalanceExclWithdrawalPending ?? 0).gt(0);
}
